import { useMemo, useState } from "react";
import {
  EllipsisVertical,
  Image as ImageIcon,
  ImagePlus,
  MessageCircle,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  Sparkles,
  Sun,
  Trash2,
  UserRound,
} from "lucide-react";
import { IconButton } from "../components/ui/Primitives.jsx";
import { formatConversationTime } from "../lib/format.js";

const SIDEBAR_NAV_ITEMS = [
  { id: "image", label: "生图", icon: Sparkles },
  { id: "gallery", label: "作品图集", icon: ImageIcon },
];

export function AppSidebar({
  title,
  logoSvg,
  logoUrl,
  authInfo,
  activeWorkspace,
  conversations,
  activeConversationId,
  sidebarCollapsed,
  theme,
  onNavigate,
  onNewConversation,
  onSelectConversation,
  onAuthRequest,
  onDeleteConversation,
  onSidebarToggle,
  onThemeToggle,
}) {
  const [openActionsId, setOpenActionsId] = useState("");
  const isAuthenticated = Boolean(authInfo?.authenticated);
  const user = authInfo?.user || null;
  const darkMode = theme === "dark";
  const navItems = isAuthenticated
    ? SIDEBAR_NAV_ITEMS
    : SIDEBAR_NAV_ITEMS.filter((item) => item.id !== "gallery");
  const recentConversations = useMemo(
    () => [...(conversations || [])]
      .filter((item) => {
        const hasTitle = String(item.title || "").trim().length > 0;
        const hasMessages = Array.isArray(item.messages) && item.messages.length > 0;
        return hasTitle || hasMessages;
      })
      .sort((left, right) => Number(right.updatedAt || 0) - Number(left.updatedAt || 0))
      .slice(0, 6),
    [conversations],
  );

  return (
    <aside className={isAuthenticated ? "frontend-sidebar" : "frontend-sidebar is-anonymous"} aria-label="前台导航">
      <div className="sidebar-brand">
        {sidebarCollapsed ? (
          <button
            className="sidebar-brand-main sidebar-brand-logo-toggle"
            type="button"
            aria-label="展开侧边栏"
            title="展开侧边栏"
            onClick={onSidebarToggle}
          >
            <BrandLogo logoSvg={logoSvg} logoUrl={logoUrl} />
            <span className="sidebar-brand-hover-icon" aria-hidden="true">
              <PanelLeftOpen size={18} />
            </span>
          </button>
        ) : (
          <>
            <div className="sidebar-brand-main" title={title}>
              <BrandLogo logoSvg={logoSvg} logoUrl={logoUrl} />
              <span className="sidebar-brand-title">{title}</span>
            </div>
            <IconButton
              className="sidebar-collapse-button"
              aria-label="收起侧边栏"
              aria-expanded={!sidebarCollapsed}
              title="收起侧边栏"
              onClick={onSidebarToggle}
            >
              <PanelLeftClose size={18} aria-hidden="true" />
            </IconButton>
          </>
        )}
      </div>

      <div className="sidebar-nav-panel">
        <button className="sidebar-new-button" type="button" title="开启新对话" onClick={onNewConversation}>
          <Plus size={21} aria-hidden="true" />
          <span className="sidebar-label">开启新对话</span>
        </button>

        <nav className="sidebar-nav" aria-label="快捷导航">
          <ul className="sidebar-nav-list">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.id === activeWorkspace;
              return (
                <li className="sidebar-nav-item-shell" key={item.id}>
                  <button
                    className={isActive ? "sidebar-nav-item active" : "sidebar-nav-item"}
                    type="button"
                    aria-current={isActive ? "page" : undefined}
                    data-state={isActive ? "active" : "idle"}
                    title={item.label}
                    onClick={() => onNavigate(item.id)}
                  >
                    <Icon size={20} aria-hidden="true" />
                    <span className="sidebar-label">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {isAuthenticated && recentConversations.length > 0 ? (
        <section className="sidebar-recent" aria-label="最近会话">
          <div className="sidebar-section-heading">
            <span>最近会话</span>
            <IconButton className="sidebar-search-button" aria-label="搜索会话">
              <Search size={18} aria-hidden="true" />
            </IconButton>
          </div>

          <div className="sidebar-recent-list" role="list">
            {recentConversations.map((item) => {
              const isActive = activeWorkspace === "chat" && item.id === activeConversationId;
              const actionsOpen = openActionsId === item.id;
              return (
                <article
                  className={isActive ? "sidebar-recent-item active" : "sidebar-recent-item"}
                  data-state={isActive ? "active" : "idle"}
                  data-actions={actionsOpen ? "open" : "closed"}
                  key={item.id}
                  role="listitem"
                  title={item.title}
                  onKeyDown={(event) => {
                    if (event.key === "Escape" && actionsOpen) {
                      setOpenActionsId("");
                    }
                  }}
                >
                  <button
                    className="sidebar-recent-primary"
                    type="button"
                    aria-label={`打开最近会话：${item.title}`}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => {
                      setOpenActionsId("");
                      onSelectConversation(item.id);
                    }}
                  >
                    <MessageCircle size={17} aria-hidden="true" />
                    <span>{item.title}</span>
                    <time>{formatConversationTime(item.updatedAt)}</time>
                  </button>
                  <span className="sidebar-recent-actions">
                    <IconButton
                      className="sidebar-recent-menu"
                      aria-label={actionsOpen ? `收起会话操作：${item.title}` : `管理会话：${item.title}`}
                      title="管理会话"
                      aria-expanded={actionsOpen}
                      onClick={() => setOpenActionsId((current) => (current === item.id ? "" : item.id))}
                    >
                      <EllipsisVertical size={17} aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      className="sidebar-recent-delete"
                      aria-label={`删除会话：${item.title}`}
                      title="删除会话"
                      aria-hidden={!actionsOpen}
                      tabIndex={actionsOpen ? 0 : -1}
                      onClick={() => {
                        onDeleteConversation(item.id);
                        setOpenActionsId("");
                      }}
                    >
                      <Trash2 size={15} aria-hidden="true" />
                    </IconButton>
                  </span>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      <div className="sidebar-account-dock">
        <button
          className="sidebar-account-button"
          type="button"
          aria-label={user ? "打开账户中心" : "打开登录窗口"}
          title={user ? "打开账户中心" : "登录账户"}
          onClick={() => onAuthRequest(user ? "account" : "login")}
        >
          <span className="sidebar-account-avatar" aria-hidden="true">
            <UserRound size={18} />
            <span className={`avatar-status ${user ? "is-online" : ""}`} />
          </span>
          <span className="sidebar-account-copy">
            <strong>{user?.username || "未登录账户"}</strong>
            <span>{getSidebarAccountMeta(authInfo)}</span>
          </span>
        </button>
        {!sidebarCollapsed ? (
          <IconButton
            className="sidebar-theme-toggle"
            aria-label={darkMode ? "切换到浅色模式" : "切换到深色模式"}
            aria-pressed={darkMode}
            title={darkMode ? "浅色模式" : "深色模式"}
            onClick={onThemeToggle}
          >
            {darkMode ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
          </IconButton>
        ) : null}
      </div>
    </aside>
  );
}

function BrandLogo({ logoSvg, logoUrl }) {
  const safeSvg = useMemo(() => sanitizeSvg(logoSvg), [logoSvg]);
  if (safeSvg) {
    return <span className="logo-svg" dangerouslySetInnerHTML={{ __html: safeSvg }} />;
  }
  const safeLogoUrl = String(logoUrl || "").trim();
  if (safeLogoUrl) {
    return <img className="logo-image" src={safeLogoUrl} alt="" decoding="async" />;
  }
  return (
    <span className="logo-fallback">
      <ImagePlus size={20} />
    </span>
  );
}

function getSidebarAccountMeta(authInfo) {
  if (authInfo?.authenticated && authInfo.user) {
    const credits = Number(authInfo.user.credits || 0);
    return `账户积分 ${Number.isFinite(credits) ? credits.toLocaleString("zh-CN") : "0"}`;
  }
  if (authInfo?.guest?.allowed) {
    const remaining = Number(authInfo.guest.remaining || 0);
    return `访客额度 ${Number.isFinite(remaining) ? remaining.toLocaleString("zh-CN") : "0"} 次`;
  }
  if (!authInfo) {
    return "正在同步状态";
  }
  return "登录后同步账户";
}

function sanitizeSvg(svgText) {
  const raw = String(svgText || "").trim();
  if (!raw || typeof DOMParser === "undefined") {
    return "";
  }
  const doc = new DOMParser().parseFromString(raw, "image/svg+xml");
  const svg = doc.documentElement;
  if (!svg || svg.tagName.toLowerCase() !== "svg" || svg.querySelector("parsererror")) {
    return "";
  }
  svg.querySelectorAll("script").forEach((node) => node.remove());
  [svg, ...svg.querySelectorAll("*")].forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      if (attribute.name.toLowerCase().startsWith("on")) {
        node.removeAttribute(attribute.name);
      }
    });
  });
  svg.setAttribute("aria-hidden", "true");
  return new XMLSerializer().serializeToString(svg);
}
